/**
 * MAChart.jsx — dashboard/MAChart.jsx
 *
 * Daily close chart with 50-day and 200-day simple moving averages.
 * Built with Recharts. Data comes from /api/chart (Yahoo Finance daily bars).
 *
 * SMA calculation is done client-side in src/lib/indicators.js sma().
 * The header shows each MA value and how far current price is from it (%).
 *
 * Note: The rightmost bar is today's partial bar if markets are open.
 * Pivot points use bars[length-2] (previous completed day) to avoid this.
 */

import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { sma } from '../../lib/indicators';
import { fmtPrice } from '../../lib/format';

export default function MAChart({ bars, symbol, currentPrice }) {
  // Build chart series with rolling SMAs computed at each bar
  const data = useMemo(() => {
    if (!bars?.length) return [];
    const closes = bars.map(b => b.c);
    return bars.map((b, i) => {
      const slice50  = closes.slice(Math.max(0, i - 49), i + 1);
      const slice200 = closes.slice(Math.max(0, i - 199), i + 1);
      return {
        date:  new Date(b.t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }),
        close: b.c,
        ma50:  slice50.length  === 50  ? sma(slice50,  50)  : null,
        ma200: slice200.length === 200 ? sma(slice200, 200) : null,
      };
    });
  }, [bars]);

  const last  = data[data.length - 1];
  const ma50  = last?.ma50;
  const ma200 = last?.ma200;

  // % distance from current price to each MA
  const vs50  = ma50  && currentPrice ? ((currentPrice - ma50)  / ma50  * 100) : null;
  const vs200 = ma200 && currentPrice ? ((currentPrice - ma200) / ma200 * 100) : null;

  return (
    <div className="chart-outer">
      {/* MA values + % distance */}
      <div className="chart-ma-bar">
        <div className="ma-pill">
          <span className="lbl">50MA</span>
          <span className="mono" style={{ color: 'var(--accent)' }}>{fmtPrice(ma50)}</span>
          {vs50 != null && (
            <span className="faint mono" style={{ fontSize: 10 }}>
              ({vs50 >= 0 ? '+' : ''}{vs50.toFixed(1)}%)
            </span>
          )}
        </div>
        <div className="ma-pill">
          <span className="lbl">200MA</span>
          <span className="mono" style={{ color: 'var(--warn)' }}>{fmtPrice(ma200)}</span>
          {vs200 != null && (
            <span className="faint mono" style={{ fontSize: 10 }}>
              ({vs200 >= 0 ? '+' : ''}{vs200.toFixed(1)}%)
            </span>
          )}
        </div>
      </div>

      <div className="chart-inner">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
            <XAxis dataKey="date" stroke="var(--faint)" tick={{ fontSize: 9, fontFamily: 'var(--mono)' }} tickLine={false} minTickGap={50} />
            <YAxis domain={['auto', 'auto']} stroke="var(--faint)" tick={{ fontSize: 9, fontFamily: 'var(--mono)' }} tickLine={false} axisLine={false} width={44} tickFormatter={v => v.toFixed(0)} />
            <Tooltip
              contentStyle={{ background: 'var(--bg-elev)', border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono)' }}
              labelStyle={{ color: 'var(--muted)' }}
              formatter={(v, name) => [v == null ? '—' : v.toFixed(2), name]}
            />
            <Line type="monotone" dataKey="close" stroke="var(--text)"   strokeWidth={1.5} dot={false} name="Close" />
            <Line type="monotone" dataKey="ma50"  stroke="var(--accent)" strokeWidth={1}   dot={false} name="50MA" />
            <Line type="monotone" dataKey="ma200" stroke="var(--warn)"   strokeWidth={1}   dot={false} name="200MA" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
